import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Bottom sheet (mobile) / centered panel (desktop). SOLID surface — sheets are
 * never glass. Used for replay detail, template preview, Ask Nara, etc.
 *
 * Rendered through a portal to <body> on purpose: the app's <main> is a
 * `relative z-10` stacking context, so a sheet mounted inside it would be
 * trapped below the floating nav (z-50) no matter how high its own z-index —
 * its bottom rows would sit *behind* the nav and be unclickable. Portaling to
 * body lets z-[60] actually win over the nav (and the backdrop dims it too).
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-ink-strong/40 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          "relative max-h-[88vh] w-full overflow-y-auto rounded-t-sheet bg-paper px-5 pt-5 shadow-md animate-fade-up",
          // generous bottom padding so the last rows clear the home indicator
          "pb-[max(env(safe-area-inset-bottom),1.5rem)]",
          "sm:m-4 sm:max-w-lg sm:rounded-sheet sm:pb-5",
          className,
        )}
      >
        {title && (
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[16px] font-semibold tracking-tight text-ink-strong">{title}</h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="grid h-8 w-8 place-items-center rounded-full text-muted hover:bg-paper-2 hover:text-ink dark:hover:bg-white/5"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
